import { isNative as isNativeFromJWeb } from '../../services/JWeb';
import {
  getContainers,
  getRepositories,
  getServices
} from '../../infra/startup';
import SubscriberRestartTenantServices from './subscriberRestartTenantServices';
import subscriberExchangeTokenToUpdateValueStore from './subscriberTokenToValueStore';
import SetFirstApplicationToken from './setFirstApplicationToken';
import SetSupportSessionToken from './setSupportSessionToken';
import CleanUpEnvironment from './cleanUpEnvironment';
import UsePostRedirectService from './usePostRedirect';
import ServiceWorkerRules from './ServiceWorkerRules';
import EventsBehaviors from './EventsBehaviors';
import RemoveMaxAgeCookieInLoggedIn from './removeMaxAgeCookieInLoggedIn';
import {
  RefreshWhenAuthStatusChanged,
  initialNavigationStateRepository
} from './RefreshWhenNativeAuthStatusChanged';
import { MultipleTabsDeamon } from './MultipleTabsDeamon';
import SetFirstTenantForNative from './SetFirstTenantForNative';
import { internalLogger } from '../../interface/v1/clientOsLogger';
import { container } from 'tsyringe';
import { ShellFlagsInputType } from '../../infra/startup/types';
import ForceReloadAfterUserOnboardingPath from './forceReloadAfterUserOnboardingPath';
import NavigationElementsBeforeInitBehavior from './NavigationElementsBeforeInitBehavior';
import { ServicePriority } from '../../core/containers/IContainer';
import RenewTenantHandlerKey from './renewTenantHandlerKey';
import RefreshNavMfe from './RefreshNavMfe';
import AdvanceRequests from './advanceRequests';
import { ServicesType } from '../../infra/services/ServicesType';
import InitializationTokenCache from '../../services/JWeb/cache/InitializationTokenCache';

export default class BeforeInitBehaviors {
  public static async init(): Promise<void> {
    const isNative = await isNativeFromJWeb();

    const repositories = getRepositories();
    const { lastTenantIdsRepository } = repositories;

    const services = getServices();
    const {
      navigationService, // already init? Yes
      sessionService, // already init? Yes
      authTokenService, // already ready? Yes
      supportSessionService, // already ready? Yes
      tenantHandlerService, // already init? Yes
      applicationService, // already ready? Yes
      stateParamHandlerService, // already ready? Yes
      routesService, // already ready? Yes
      userOnboardingService, // already ready? Yes
      serviceWorkerService, // already init? No <<<<<<
      eventService // already init? yes
    } = services;

    const { authRefreshContainer, accessControlContainer } = getContainers();

    const removeMaxAgeCookieInLoggedIn = new RemoveMaxAgeCookieInLoggedIn();

    const shellFlagsProps =
      container.resolve<ShellFlagsInputType>('ShellFlagsProps');

    const web_setFirstApplicationToken =
      !isNative &&
      new SetFirstApplicationToken({
        sessionService,
        authTokenService,
        supportSessionService,
        shouldRefreshToken: shellFlagsProps?.enableTokenRefreshOnInit
      });

    const web_setSupportSessionToken =
      !isNative &&
      new SetSupportSessionToken({ supportSessionService, authTokenService });

    const subscriberRestartTenantServices = new SubscriberRestartTenantServices(
      {
        accessControlContainer
      }
    );

    const web_writeCookieDataToValueStore =
      !isNative && new subscriberExchangeTokenToUpdateValueStore();

    const cleanUpEnvironment = new CleanUpEnvironment({
      sessionService
    });

    const usePostRedirectService = new UsePostRedirectService({
      stateParamHandlerService
    });

    const events = new EventsBehaviors({
      eventService,
      navigationService
    });

    const serviceWorkerRules = new ServiceWorkerRules({
      serviceWorkerService,
      sessionService
    });

    const native_setFirstTenantForNative =
      isNative &&
      new SetFirstTenantForNative({
        authTokenService,
        tenantHandlerService,
        applicationService
      });

    const refreshWhenAuthStatusChanged = new RefreshWhenAuthStatusChanged({
      //parameters
      isNative,
      webUIRefreshOnAuthChange: shellFlagsProps?.webUIRefreshOnAuthChange,
      refreshScreenOnChange: shellFlagsProps?.nativeRefreshListener,
      nativeRefreshServicesOnAuthChange:
        shellFlagsProps?.nativeRefreshServicesOnAuthChange,
      //repository
      initialNavigationStateRepository,
      //services
      eventService,
      navigationService,
      tenantHandlerService,
      sessionService,
      // containers
      authRefreshContainer
    });

    // This also responsible to SetTenant for the first time (On Native)
    const multipleTabsDeamon = new MultipleTabsDeamon({
      repository: lastTenantIdsRepository,
      isNative: isNative
    });

    const forceReloadAfterUserOnboardingPath =
      new ForceReloadAfterUserOnboardingPath({
        navigation: navigationService,
        userOnboardingService
      });

    // TODO: split these codes below in a isolated method
    if (!isNative) {
      const refreshNavMfe = new RefreshNavMfe({
        webUIRefreshOnAuthChange: shellFlagsProps?.webUIRefreshOnAuthChange
      });
      authRefreshContainer.addService(refreshNavMfe, ServicePriority.LOW);
      // avoid using accessControlContainer because it is also trigged when the tenant changes;
    }
    if (isNative) {
      // Restart Token Cache Initialization on Native Auth Change
      internalLogger?.debug?.(
        'Setting token Cache Initialization trigger for Native Auth Change.'
      );

      authRefreshContainer.addRefreshServices(
        InitializationTokenCache.restartCache,
        ServicePriority.VITAL,
        'InitializationTokenCache'
      );
      authRefreshContainer.addRefreshServices(
        InitializationTokenCache.killCache,
        ServicePriority.MESSAGE,
        'InitializationTokenCache'
      );

      const native_renewTenantHandlerKey = new RenewTenantHandlerKey({
        routesService,
        navigationService,
        tenantHandlerService
      });
      authRefreshContainer.addService(sessionService, ServicePriority.CRITICAL);

      // TODO: maybe this is won't be needed in the future
      authRefreshContainer.addService(
        native_renewTenantHandlerKey,
        ServicePriority.HIGH
      );
    }

    const advanceRequests = new AdvanceRequests({
      tenantHandlerService,
      navigationService,
      sessionService
    });

    /**
     *  Execution phase
     * We need instantiation + execution because await is not allowed in the constructor
     */
    internalLogger?.debug?.('Starting ClientOS Behaviors');

    // 1. Cleaners
    removeMaxAgeCookieInLoggedIn?.init?.();
    cleanUpEnvironment?.init?.();

    // 2. Setters
    multipleTabsDeamon.init();

    forceReloadAfterUserOnboardingPath.init();
    await subscriberRestartTenantServices?.init?.();
    await web_setFirstApplicationToken?.init?.();
    await web_setSupportSessionToken?.init?.();
    await web_writeCookieDataToValueStore?.init?.();
    await native_setFirstTenantForNative?.init?.();

    // this behavior can redirect the application
    usePostRedirectService.init?.();

    // ServiceWorkerRules behavior
    // 1. What: Clean up
    // 2. When: user is logged out
    // 3. Where: Just in Service Worker
    // TODO: this should be async.
    serviceWorkerRules?.init?.();

    events?.init?.();
    await refreshWhenAuthStatusChanged?.init?.();
    internalLogger?.debug?.('ClientOS Behaviors initialized.');

    new NavigationElementsBeforeInitBehavior()?.init?.();

    await this.handlePostBeforeInitBehaviorsInit(services);

    void advanceRequests.init();
  }

  /**
   * Handles post-beforeInitBehaviors initialization setup and routing
   */
  private static async handlePostBeforeInitBehaviorsInit(
    services: ServicesType
  ): Promise<void> {
    const { navigationService, routesService, tenantHandlerService } = services;

    try {
      // Handle route-specific tenant configuration
      const currentPath = navigationService.location.pathname;
      console.debug(`Processing current route: ${currentPath}`);

      const routeInfo = await routesService?.findRouteWithPriority?.(
        currentPath,
        false
      );
      const { tenantHandlerOverride } = routeInfo || {};

      await tenantHandlerService?.setTenantHandlerKey(tenantHandlerOverride);
    } catch (error) {
      console.error('Post-initialization setup failed:', error);
      throw new Error(`Post-initialization setup failed: ${error.message}`);
    }
  }
}
